/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 29/04/2025 17:28
 *
 */

import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ActionsBarBuilder,
  SlidingMenuComponent,
} from '@infodata-group/inf-commons-desktop';
import { FormsModule } from '@angular/forms';
import { ChatMessage } from './model/chat-message';
import { Chat } from './model/chat';
import {
  ActionEvent,
  RestService,
  StandardAction,
} from '@infodata-group/inf-commons';
import { ChatMessageFactory } from './factory/chat-message.factory';

const CLOSE = new StandardAction('close', 'times', '', 'Fermer le chat');
const ERASE = new StandardAction(
  'erase',
  'eraser',
  '',
  'Réinitialiser le chat',
);

@Component({
  selector: 'infl-infodata-bot',
  standalone: true,
  imports: [CommonModule, FormsModule, SlidingMenuComponent],
  templateUrl: './infodata-bot.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './infodata-bot.scss',
})
export class InfodataBot implements OnChanges {
  @Input() openBot: boolean;

  public config = new ActionsBarBuilder()
    .setStyle('classic')
    .setIsTitleBold(true)
    .setMarginLeft(20)
    .build();
  public actions = [ERASE, CLOSE];
  public response = false;
  public userMessageId = '';
  public newMessage = '';
  public messages: ChatMessage[];
  public restMessageList: ChatMessage[];
  public isFooterFocus = false;

  @Output() closeBot: EventEmitter<ActionEvent> = new EventEmitter();

  @ViewChild('botSliding') botSliding: SlidingMenuComponent;
  @ViewChild('chatContent') chatContent: ElementRef<HTMLDivElement>;

  private restService = inject(RestService);

  constructor() {
    this.initChat();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);
    if (Object.hasOwn(changes, 'openBot')) {
      this.openBot ? this.botSliding.open() : this.botSliding.close();
    }
  }

  initChat() {
    this.messages = [];
    this.messages = new Chat().defaultList.reverse();
    this.restService.getAll(new ChatMessageFactory()).subscribe({
      next: (messages) => {
        this.restMessageList = messages;
        this.checkList('default');
      },
    });
  }

  onActionClick(event: string) {
    switch (event) {
      case 'close':
        this.closeBot.emit(
          new ActionEvent('click', new StandardAction(event, '')),
        );
        this.botSliding.close();
        break;
      case 'erase':
        this.initChat();
        break;
      default:
        console.log('Action not allowed');
    }
  }

  sendMessage() {
    if (this.newMessage) {
      if (this.newMessage === 'start') {
        this.initChat();
        this.newMessage = '';
        return;
      }

      this.userMessageId = `user-${(this.messages.length + 1).toString()}`;
      const message = new ChatMessage(
        this.userMessageId,
        this.newMessage,
        'user',
      );
      this.messages.unshift(message);

      // Simuler une réponse automatique de l'interlocuteur
      setTimeout(() => {
        this.createBotMessage(message.id);
      }, 2000);
      this.newMessage = '';
    }
  }

  selectAction(actionMessage: ChatMessage) {
    const selectedActionMessage = new ChatMessage(
      actionMessage.id,
      actionMessage.content,
      'user',
    );
    this.messages.unshift(selectedActionMessage);

    setTimeout(() => {
      this.checkList(actionMessage.id, actionMessage.content);
    }, 1000);
  }

  checkList(actionMessageId: string, actionMessageContent?: string) {
    this.response = false;
    const result = this.restMessageList.filter(
      (m: ChatMessage) => m.origin === actionMessageId,
    );
    const response = result.filter((m: ChatMessage) => m.response);

    if (response.length > 0) {
      response.forEach((m: ChatMessage) => {
        m.sender = 'response';
      });
      this.messages = [...response.reverse(), ...this.messages];
      this.response = true;
      return;
    }
    if (actionMessageId !== 'default') {
      this.createBotMessage(actionMessageId, actionMessageContent);
    }
    this.messages = [...result.reverse(), ...this.messages];
  }

  createBotMessage(messageId: string, messageContent?: string) {
    let botMessage = new ChatMessage(
      `bot-${this.userMessageId.substring(5)}`,
      `Réponse automatique`,
      'bot',
    );
    if (messageContent) {
      botMessage = new ChatMessage(
        `bot-${messageId}`,
        `Que souhaitez-vous savoir sur : "${messageContent}" ?`,
        'bot',
      );
    }
    this.messages.unshift(botMessage);
  }

  onFocus() {
    this.isFooterFocus = !this.isFooterFocus;
  }
}
